import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { omit } from "lodash-es";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import User from "@mapmycustomers/shared/types/User";

import CreatorFilter from "@app/component/CreatorFilter/CreatorFilter";
import RoundButton from "@app/component/RoundButton";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import { getResourcesViewState } from "../../store";
import { fetchResources } from "../../store/actions";

import messages from "./messages";
import ResourceTypeFilter from "./ResourceTypeFilter";
import styles from "./ToolBar.module.scss";

type FiltersProps = {
  isSharedFolder: boolean;
  isTrash: boolean;
} & typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>;

const Filters: FC<FiltersProps> = ({
  currentUserId,
  isSharedFolder,
  isTrash,
  onFetchResources,
  viewState,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const { filter, range } = viewState;

  const firstPageRange = useMemo(
    () => ({ endRow: range.endRow - range.startRow, startRow: 0 }),
    [range.endRow, range.startRow]
  );

  const [selectedUserIds, sharedWithUserIds] = useMemo(() => {
    const selected: User["id"][] = [];
    const shared: User["id"][] = [];

    const ownerCondition = filter[ResourceFieldName.OWNER];
    if (
      ownerCondition &&
      isSimpleCondition(ownerCondition) &&
      ownerCondition.operator === FilterOperator.CONTAINS
    ) {
      selected.push(...ownerCondition.value);
    }

    const sharedCondition = filter[ResourceFieldName.SHARED_WITH];
    if (
      sharedCondition &&
      isSimpleCondition(sharedCondition) &&
      sharedCondition.operator === FilterOperator.CONTAINS
    ) {
      shared.push(...sharedCondition.value);
    }

    return [selected, shared];
  }, [filter]);

  const [myFilesActive, sharedWithMeActive] = useMemo(
    () => [
      selectedUserIds.length === 1 && selectedUserIds[0] === currentUserId,
      sharedWithUserIds.length === 1 &&
        sharedWithUserIds[0] === currentUserId &&
        !selectedUserIds.length,
    ],
    [currentUserId, selectedUserIds, sharedWithUserIds]
  );

  const handleFilterMyFiles = useCallback(() => {
    analytics.clicked(["My Files"]);
    onFetchResources({
      filter: myFilesActive
        ? omit(filter, ResourceFieldName.OWNER)
        : {
            ...omit(filter, ResourceFieldName.SHARED_WITH),
            [ResourceFieldName.OWNER]: {
              operator: FilterOperator.CONTAINS,
              value: [currentUserId],
            },
          },
      range: firstPageRange,
    });
  }, [analytics, onFetchResources, myFilesActive, filter, currentUserId, firstPageRange]);

  const handleFilterSharedWithMe = useCallback(() => {
    analytics.clicked(["Shared With Me"]);
    onFetchResources({
      filter: sharedWithMeActive
        ? omit(filter, ResourceFieldName.OWNER, ResourceFieldName.SHARED_WITH)
        : {
            ...filter,
            [ResourceFieldName.OWNER]: {
              operator: FilterOperator.NOT_EQUAL,
              value: currentUserId,
            },
            [ResourceFieldName.SHARED_WITH]: {
              operator: FilterOperator.CONTAINS,
              value: [currentUserId],
            },
          },
      range: firstPageRange,
    });
  }, [analytics, onFetchResources, sharedWithMeActive, filter, currentUserId, firstPageRange]);

  const handleChangeOwnerFilter = useCallback(
    (userIds: User["id"][]) => {
      onFetchResources({
        filter: {
          [ResourceFieldName.OWNER]: { operator: FilterOperator.CONTAINS, value: userIds },
          [ResourceFieldName.SHARED_WITH]: {
            operator: FilterOperator.CONTAINS,
            value: [currentUserId],
          },
        },
      });
    },
    [currentUserId, onFetchResources]
  );

  return (
    <div className={styles.left}>
      <ResourceTypeFilter />
      {!isTrash && !isSharedFolder && (
        <>
          <CreatorFilter
            label={intl.formatMessage(messages.owner)}
            onChange={handleChangeOwnerFilter}
            selectedUserIds={selectedUserIds}
          />
          <RoundButton active={myFilesActive} onClick={handleFilterMyFiles}>
            {intl.formatMessage(messages.myFiles)}
          </RoundButton>
          <RoundButton active={sharedWithMeActive} onClick={handleFilterSharedWithMe}>
            {intl.formatMessage(messages.sharedWithMe)}
          </RoundButton>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  viewState: getResourcesViewState(state),
});

const mapDispatchToProps = {
  onFetchResources: fetchResources.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
